/* eslint-disable no-underscore-dangle */
import { Fragment, ReactElement } from "react";
import {
  InternalDocSearchHit,
  StoredDocSearchHit,
} from "@docsearch/react/dist/esm/types";
import { Config } from "@configured/EnvironmentConfig";
import { SmartLink } from "@components/doc/SmartLink";
import theme from "@config/theme";
import SectionIcon from "./icons/SectionIcon";
import PageIcon from "./icons/PageIcon";

const debug = false;
const showURL = debug;
const showJSON = debug;

// Decode things like &lt; to <
function decodeHtmlEntities(str: string) {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
}

export function mark(snippet: string) {
  const parts = [] as (ReactElement | string)[];
  let t = snippet;
  let key = 0;
  while (t.includes("<mark>") && t.includes("</mark>")) {
    const start = t.indexOf("<mark>");
    const end = t.indexOf("</mark>");
    if (start > 0) {
      parts.push(
        <Fragment key={key}>
          {decodeHtmlEntities(t.substring(0, start))}
        </Fragment>,
      );
      key += 1;
    }
    parts.push(
      <mark key={key}>{decodeHtmlEntities(t.substring(start + 6, end))}</mark>,
    );
    key += 1;
    t = t.substring(end + 7);
  }
  parts.push(<Fragment key={key}>{decodeHtmlEntities(t)}</Fragment>);
  return <>{parts}</>;
}

// type guard to prove type is lvl0, lvl1, lvl2, lvl3, lvl4, lvl5, or lvl6
const isHierarchy = (
  type: string,
): type is "lvl0" | "lvl1" | "lvl2" | "lvl3" | "lvl4" | "lvl5" | "lvl6" =>
  ["lvl0", "lvl1", "lvl2", "lvl3", "lvl4", "lvl5", "lvl6"].includes(type);

// type guard to prove type is 'content'
const isContent = (type: string): type is "content" => type === "content";

// type guard to prove hit has _snippetResult
const hasSnippet = (
  hit: InternalDocSearchHit | StoredDocSearchHit,
): hit is InternalDocSearchHit => "_snippetResult" in hit;

// type guard to prove hit has _highlightResult
const hasHighlight = (
  hit: InternalDocSearchHit | StoredDocSearchHit,
): hit is InternalDocSearchHit => "_highlightResult" in hit;

// type guard to prove hit has hierarchy
const hasHierarchy = (
  hit: InternalDocSearchHit | StoredDocSearchHit,
): hit is InternalDocSearchHit => "hierarchy" in hit;

export const getSnippet = (
  hit: InternalDocSearchHit | StoredDocSearchHit,
  fallback = "[snippet not available]",
) => {
  const { type } = hit;

  try {
    const candidates = [
      {
        prefix: "snippet-hierarchy",
        value:
          hasSnippet(hit) &&
          isHierarchy(type) &&
          hit._snippetResult.hierarchy?.[type]?.value,
      },
      {
        prefix: "snippet-hierarchy-camel",
        value:
          hasSnippet(hit) &&
          isHierarchy(type) &&
          hit._snippetResult.hierarchy_camel?.[0]?.[type]?.value,
      },
      {
        prefix: "snippet-content",
        value:
          hasSnippet(hit) &&
          isContent(type) &&
          hit._snippetResult?.content?.value,
      },
      {
        prefix: "highlight-hierarchy",
        value:
          hasHighlight(hit) &&
          isHierarchy(type) &&
          hit._highlightResult?.hierarchy?.[type]?.value,
      },
      {
        prefix: "highlight-hierarchy-camel",
        value:
          hasHighlight(hit) &&
          isHierarchy(type) &&
          hit._highlightResult?.hierarchy_camel?.[0]?.[type]?.value,
      },
      {
        prefix: "highlight-content",
        value:
          hasHighlight(hit) &&
          isContent(type) &&
          hit._highlightResult?.content?.value,
      },
      {
        prefix: "hierarchy",
        value: hasHierarchy(hit) && isHierarchy(type) && hit.hierarchy?.[type],
      },
      {
        prefix: "content",
        value: hit.content,
      },
    ];

    let snippet = fallback;

    candidates.reverse().forEach((candidate) => {
      if (candidate.value) {
        snippet = `${candidate.value}`;
        if (debug) {
          snippet = `${candidate.prefix}: ${snippet}`;
        }
      }
    });
    return snippet;
  } catch (e) {
    return `${fallback} + ${e}`;
  }
};

const getMarks = (snippet: string) => {
  // combine adjacent marks
  const s = snippet.replace(/<\/mark>\s*<mark>/g, " ");
  const markRegex = /<mark>(.*?)<\/mark>/g;
  const marks = [...s.matchAll(markRegex)].map((match) => match[1]);
  return marks;
};

export const betterUrlLookup: Record<string, string> = {};

// ResultLink is a Blues component created in the spirit of Algolia's DocSearch.
// That is, in order to keep the DocSearch styling and keyboard navigation we're
// using many of the same classes they usually use.
export const ResultLink = (props: {
  hit: InternalDocSearchHit | StoredDocSearchHit;
}) => {
  const { hit } = props;

  const snippet = getSnippet(hit);

  const urlObj = new URL(hit.url, Config.siteURL);

  // Turn absolute links into relative links to make SmartLink happy.
  const urlStr = urlObj.toString().replace(Config.siteURL, "");

  betterUrlLookup[hit.url] = urlStr;

  // Get the (abbreviated) path from the hierarchy.
  const { lvl0, lvl1, lvl2 } = hit.hierarchy; // Ignore lvl3, lvl4, lvl5, lvl6
  let path = [lvl1, lvl2].filter(Boolean).join(" > ");
  path = decodeHtmlEntities(path);

  const iconForType = (type: string, level0: string) => {
    switch (type) {
      case "lvl0":
        return <PageIcon />;
      case "lvl1":
        if (level0 === "Docs") return <PageIcon />;
        return <SectionIcon />;
      case "lvl2":
      case "lvl3":
      case "lvl4":
      case "lvl5":
      case "lvl6":
      case "content":
      default:
        return <SectionIcon />;
    }
  };

  return (
    <>
      <SmartLink href={`${urlStr}`}>
        <div className="DocSearch-Hit-Container">
          <div className="DocSearch-Hit-icon">
            {iconForType(hit.type, lvl0)}
          </div>
          <div className="DocSearch-Hit-content-wrapper">
            <span className="DocSearch-Hit-title">
              {path} {showURL ? `(${urlStr})` : ""}
              {
                showJSON ? <><br />{JSON.stringify(hit)}</> : "" /* prettier-ignore */
              }
            </span>
            <span className="DocSearch-Hit-path">
              {/* … notecard ATTN pin and a <mark>GPI</mark>O pin on the host … */}
              <span>{mark(snippet)}</span>
            </span>
          </div>
        </div>
      </SmartLink>
      <style jsx global>
        {`
          .DocSearch-Modal {
            // Some other styles we may want to override:
            // --docsearch-main-color: #458ee1;
            // --docsearch-layout-type: normal;
            // --docsearch-layout-width: normal;
            // --docsearch-layout-alignment: align;
            // --docsearch-background-color: #fff;
            // --docsearch-border-radius: 4;
            // --docsearch-border-width: 1;
            // --docsearch-border-color: #d9d9d9;
            // --docsearch-box-shadow: light;
            // --docsearch-branding-position: bottom;
            // --docsearch-spacing: normal;
            // --docsearch-include-desc: yes;
            // --docsearch-background-category-header: #ffffff;
            // --docsearch-font-size: normal;
            // --docsearch-header-color: #33363d;
            // --docsearch-title-color: #02060c;
            // --docsearch-subtitle-color: #a4a7ae;
            // --docsearch-text-color: #63676d;
            --docsearch-highlight-color: ${theme.colors.ultramarine};
            --docsearch-primary-color: ${theme.colors.ultramarine};
            --docsearch-searchbox-shadow: inset 0 0 0 2px
              ${theme.colors.ultramarine};
            --docsearch-modal-height: 75vh;
            // --docsearch-highlight-opacity: 0.1;
            // --docsearch-highlight-type: underline;
          }

          .DocSearch-Hit a {
            // background-color: ${theme.colors.lightGray};
            border-radius: 4px;
            // transparent border
            border: 2px solid transparent;
          }
          .DocSearch-Hit[aria-selected="true"] a {
            background-color: ${theme.colors.lightGray};
            --docsearch-hit-active-color: ${theme.colors.darkCharlestonGreen};
            border-radius: 4px;
            border: 2px solid ${theme.colors.ultramarine};
          }
        `}
      </style>
    </>
  );
};
